import React, { useState } from "react";
import axios from "axios";
import { ShieldCheck, Lock, Loader2, Send, Plus, Trash2, Paperclip, X, FileText } from "lucide-react";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = ["Mr", "Mrs", "Miss", "Ms", "Mx", "Dr", "Other"];
const GENDERS = ["Female", "Male", "Prefer not to say"];

const emptyPrev = () => ({ line: "", postcode: "", country: "United Kingdom", fromDate: "", toDate: "" });

const initialForm = {
  title: "", firstName: "", middleNames: "", lastName: "", maidenName: "", otherNames: "",
  mothersMaidenName: "", dateOfBirth: "", gender: "", townOfBirth: "", countryOfBirth: "United Kingdom",
  nationality: "", nationalInsurance: "", email: "", phone: "",
  currentAddress: { line: "", postcode: "", country: "United Kingdom", fromDate: "" },
  previousAddresses: [],
  consent: false,
};

const DOC_SLOTS = [
  { key: "photoId", label: "Photo ID (passport or driving licence)" },
  { key: "proofOfAddress", label: "Proof of address (utility bill or bank statement)" },
  { key: "dbsCertificate", label: "Existing DBS certificate (optional)" },
];

const Req = () => <span className="text-[#ea731d]">*</span>;

const Onboarding = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const handleChange = (e) => setField(e.target.name, e.target.value);
  const setCurrent = (k, v) => setForm((f) => ({ ...f, currentAddress: { ...f.currentAddress, [k]: v } }));

  const addPrev = () => setForm((f) => ({ ...f, previousAddresses: [...f.previousAddresses, emptyPrev()] }));
  const removePrev = (i) => setForm((f) => ({ ...f, previousAddresses: f.previousAddresses.filter((_, idx) => idx !== i) }));
  const setPrev = (i, k, v) =>
    setForm((f) => ({
      ...f,
      previousAddresses: f.previousAddresses.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)),
    }));

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error("Please enter your access code."); return; }
    setVerifying(true);
    try {
      await axios.post(`${API}/dbs/verify-code`, { accessCode: code.trim() });
      setUnlocked(true);
    } catch (err) {
      toast.error(err.response?.status === 403 ? "That access code is not valid." : "Could not verify the code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleFile = (key, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const allowed = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".heic", ".doc", ".docx"];
    if (!allowed.some((ext) => file.name.toLowerCase().endsWith(ext))) {
      toast.error("Please upload a PDF, image or Word document.");
      e.target.value = ""; return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large (max 10MB).");
      e.target.value = ""; return;
    }
    setFiles((f) => ({ ...f, [key]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = {
      "First name": form.firstName, "Last name": form.lastName,
      "Mother's maiden name": form.mothersMaidenName, "Date of birth": form.dateOfBirth,
      "Town of birth": form.townOfBirth, "Country of birth": form.countryOfBirth,
      "National Insurance number": form.nationalInsurance, "Email": form.email, "Phone": form.phone,
      "Current address": form.currentAddress.line, "Lived here since": form.currentAddress.fromDate,
    };
    const missing = Object.entries(required).filter(([, v]) => !v || !String(v).trim()).map(([k]) => k);
    if (missing.length) { toast.error(`Please complete: ${missing.join(", ")}`); return; }
    if (!form.consent) { toast.error("Please tick the consent box to continue."); return; }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...form, accessCode: code.trim() }));
      DOC_SLOTS.forEach(({ key }) => { if (files[key]) fd.append(key, files[key]); });
      await axios.post(`${API}/dbs/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Your access code is no longer valid." : "Something went wrong. Please try again or contact us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 pt-32 pb-20">
        <div data-testid="dbs-success" className="max-w-md text-center bg-white rounded-[28px] p-10 border border-neutral-100 soft-shadow">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center">
            <ShieldCheck size={30} />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-[#1a1714]">Submission received</h1>
          <p className="mt-3 text-neutral-600">Thank you. Your DBS details have been securely submitted to the iClean NW team. We will be in touch about the next steps.</p>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 pt-32 pb-20">
        <Seo title="DBS Check Form | iClean NW" description="Secure DBS check information form for iClean NW candidates." path="/dbs" noindex />
        <form onSubmit={verifyCode} className="max-w-md w-full bg-white rounded-[28px] p-9 border border-neutral-100 soft-shadow text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center">
            <Lock size={28} />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-[#1a1714]">DBS check form</h1>
          <p className="mt-2 text-sm text-neutral-600">Enter the access code provided by the iClean NW team to continue.</p>
          <Input
            data-testid="dbs-access-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="mt-6 h-12 rounded-xl text-center tracking-widest"
            placeholder="Access code"
            autoComplete="off"
          />
          <button type="submit" disabled={verifying} data-testid="dbs-unlock-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-6 py-3 rounded-full transition-all">
            {verifying ? <><Loader2 size={17} className="animate-spin" /> Checking...</> : "Continue"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <Seo title="DBS Check Form | iClean NW" description="Secure DBS check information form for iClean NW candidates." path="/dbs" noindex />
      <section className="pt-32 lg:pt-40 pb-4">
        <div className="max-w-4xl mx-auto px-5 lg:px-6">
          <span className="inline-flex items-center gap-2 text-[#ea731d] font-semibold tracking-[0.2em] text-xs uppercase">
            <ShieldCheck size={15} /> DBS check
          </span>
          <h1 className="mt-3 text-4xl lg:text-5xl font-bold text-[#1a1714] leading-[1.05]">DBS check form</h1>
          <p className="mt-5 text-neutral-600 max-w-2xl">
            Please complete the details below for your DBS (Disclosure and Barring Service) check. Your information is submitted securely and handled in line with our data protection obligations.
          </p>
        </div>
      </section>

      <section className="py-8 lg:py-12">
        <div className="max-w-4xl mx-auto px-5 lg:px-6">
          <form onSubmit={handleSubmit} data-testid="dbs-form" className="bg-white rounded-[28px] p-7 lg:p-10 border border-neutral-100 soft-shadow">
            <p className="text-sm text-neutral-500">Fields marked <Req /> are required.</p>

            {/* Personal */}
            <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Personal details</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm font-medium">Title</Label>
                <Select value={form.title} onValueChange={(v) => setField("title", v)}>
                  <SelectTrigger data-testid="dbs-title" className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => setField("gender", v)}>
                  <SelectTrigger data-testid="dbs-gender" className="mt-2 h-12 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">First name <Req /></Label>
                <Input data-testid="dbs-firstName" name="firstName" value={form.firstName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Jane" />
              </div>
              <div>
                <Label className="text-sm font-medium">Middle name(s)</Label>
                <Input data-testid="dbs-middleNames" name="middleNames" value={form.middleNames} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Optional" />
              </div>
              <div>
                <Label className="text-sm font-medium">Last name <Req /></Label>
                <Input data-testid="dbs-lastName" name="lastName" value={form.lastName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Doe" />
              </div>
              <div>
                <Label className="text-sm font-medium">Maiden / previous surname</Label>
                <Input data-testid="dbs-maidenName" name="maidenName" value={form.maidenName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="If applicable" />
              </div>
              <div>
                <Label className="text-sm font-medium">Any other names used</Label>
                <Input data-testid="dbs-otherNames" name="otherNames" value={form.otherNames} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="If applicable" />
              </div>
              <div>
                <Label className="text-sm font-medium">Mother's maiden name <Req /></Label>
                <Input data-testid="dbs-mothersMaidenName" name="mothersMaidenName" value={form.mothersMaidenName} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="Required for DBS" />
              </div>
              <div>
                <Label className="text-sm font-medium">Date of birth <Req /></Label>
                <Input data-testid="dbs-dateOfBirth" name="dateOfBirth" type="date" value={form.dateOfBirth} onChange={handleChange} className="mt-2 h-12 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm font-medium">National Insurance number <Req /></Label>
                <Input data-testid="dbs-nationalInsurance" name="nationalInsurance" value={form.nationalInsurance} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="AB 12 34 56 C" />
              </div>
            </div>

            {/* Birth & nationality */}
            <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Place of birth</h3>
            <div className="grid sm:grid-cols-3 gap-5">
              <div>
                <Label className="text-sm font-medium">Town of birth <Req /></Label>
                <Input data-testid="dbs-townOfBirth" name="townOfBirth" value={form.townOfBirth} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="e.g. Manchester" />
              </div>
              <div>
                <Label className="text-sm font-medium">Country of birth <Req /></Label>
                <Input data-testid="dbs-countryOfBirth" name="countryOfBirth" value={form.countryOfBirth} onChange={handleChange} className="mt-2 h-12 rounded-xl" />
              </div>
              <div>
                <Label className="text-sm font-medium">Nationality</Label>
                <Input data-testid="dbs-nationality" name="nationality" value={form.nationality} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="e.g. British" />
              </div>
            </div>

            {/* Contact */}
            <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm font-medium">Email <Req /></Label>
                <Input data-testid="dbs-email" name="email" type="email" value={form.email} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="you@example.com" />
              </div>
              <div>
                <Label className="text-sm font-medium">Phone <Req /></Label>
                <Input data-testid="dbs-phone" name="phone" value={form.phone} onChange={handleChange} className="mt-2 h-12 rounded-xl" placeholder="07000 000000" />
              </div>
            </div>

            {/* Address history */}
            <h3 className="mt-8 mb-2 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Address history (last 5 years)</h3>
            <p className="text-sm text-neutral-500 mb-4">Add previous addresses so your history covers at least the last 5 years with no gaps.</p>

            <div className="rounded-2xl border border-neutral-200 p-5">
              <div className="text-sm font-semibold text-[#1a1714] mb-3">Current address <Req /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs text-neutral-500">Address</Label>
                  <Input data-testid="dbs-cur-line" value={form.currentAddress.line} onChange={(e) => setCurrent("line", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="House number and street, town" />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Postcode</Label>
                  <Input data-testid="dbs-cur-postcode" value={form.currentAddress.postcode} onChange={(e) => setCurrent("postcode", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="M1 1AA" />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Country</Label>
                  <Input data-testid="dbs-cur-country" value={form.currentAddress.country} onChange={(e) => setCurrent("country", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs text-neutral-500">Lived here since <Req /></Label>
                  <Input data-testid="dbs-cur-from" type="month" value={form.currentAddress.fromDate} onChange={(e) => setCurrent("fromDate", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                </div>
              </div>
            </div>

            {form.previousAddresses.map((a, i) => (
              <div key={i} className="mt-4 rounded-2xl border border-neutral-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-[#1a1714]">Previous address {i + 1}</div>
                  <button type="button" onClick={() => removePrev(i)} data-testid={`dbs-prev-remove-${i}`} className="text-neutral-400 hover:text-[#ea731d] transition-colors" aria-label="Remove address">
                    <Trash2 size={17} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-neutral-500">Address</Label>
                    <Input data-testid={`dbs-prev-line-${i}`} value={a.line} onChange={(e) => setPrev(i, "line", e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="House number and street, town" />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">Postcode</Label>
                    <Input value={a.postcode} onChange={(e) => setPrev(i, "postcode", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">Country</Label>
                    <Input value={a.country} onChange={(e) => setPrev(i, "country", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">From</Label>
                    <Input type="month" value={a.fromDate} onChange={(e) => setPrev(i, "fromDate", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-500">To</Label>
                    <Input type="month" value={a.toDate} onChange={(e) => setPrev(i, "toDate", e.target.value)} className="mt-1.5 h-11 rounded-xl" />
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={addPrev} data-testid="dbs-add-prev" className="mt-4 inline-flex items-center gap-2 text-[#ea731d] font-semibold text-sm hover:gap-3 transition-all">
              <Plus size={16} /> Add previous address
            </button>

            {/* Documents */}
            <h3 className="mt-8 mb-4 text-xs font-semibold uppercase tracking-widest text-[#ea731d]">Documents</h3>
            <div className="space-y-3">
              {DOC_SLOTS.map(({ key, label }) => (
                <div key={key}>
                  <Label className="text-sm font-medium">{label}</Label>
                  {!files[key] ? (
                    <label htmlFor={`doc-${key}`} className="mt-2 flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-neutral-300 hover:border-[#ea731d] hover:bg-[#ea731d]/5 transition-colors px-5 py-3.5 text-neutral-600">
                      <span className="w-9 h-9 rounded-lg bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center flex-shrink-0"><Paperclip size={17} /></span>
                      <span className="text-sm"><span className="font-semibold text-[#1a1714]">Click to upload</span> &middot; PDF, image or Word (max 10MB)</span>
                      <input id={`doc-${key}`} data-testid={`dbs-file-${key}`} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.doc,.docx" onChange={(e) => handleFile(key, e)} className="hidden" />
                    </label>
                  ) : (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-[#f7f7f5] px-5 py-3.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-9 h-9 rounded-lg bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center flex-shrink-0"><FileText size={17} /></span>
                        <span className="text-sm font-medium text-[#1a1714] truncate">{files[key].name}</span>
                      </div>
                      <button type="button" onClick={() => setFiles((f) => { const n = { ...f }; delete n[key]; return n; })} className="flex-shrink-0 text-neutral-500 hover:text-[#ea731d] transition-colors" aria-label="Remove file">
                        <X size={17} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Consent */}
            <div className="mt-8 flex items-start gap-3 rounded-2xl bg-[#fdf1e7] p-5">
              <Checkbox id="dbs-consent" data-testid="dbs-consent" checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} className="mt-0.5" />
              <label htmlFor="dbs-consent" className="text-sm text-neutral-700 leading-relaxed cursor-pointer">
                I consent to iClean NW processing this information for employment and DBS checking purposes. <Req />
              </label>
            </div>

            <button type="submit" disabled={loading} data-testid="dbs-submit-btn" className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ea731d] hover:bg-[#d9640f] disabled:opacity-70 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Submitting...</> : <><Send size={17} /> Submit securely</>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Onboarding;
